import { z } from "zod";
import { Address, MailLocation, SyncCursor } from "./docs.js";
import { KindReason, MessageKind, MessageRaw } from "./kinds.js";
import { GhGroup, GhReason, ThreadSource } from "./sources.js";
import { QueryNode } from "./query.js";
import { RuleAction } from "./rules.js";
/**
 * The normalized mail model every provider adapter speaks (architecture §6.1, §6.2). Ids here
 * are **provider** ids (Gmail ids, Graph immutable ids); the api scopes them to
 * `{accountId}:{providerId}` before they reach documents, handles or audit records.
 */
/** The account an adapter call acts for. Tokens are fetched by the adapter's token supplier. */
export const ProviderAccount = z.object({
    accountId: z.string().min(1),
    address: z.string().email(),
});
/** Who the mailbox belongs to, read once at connect (Gmail users.getProfile, Graph /me). */
export const MailboxProfile = z.object({
    address: z.string().email(),
    displayName: z.string().nullable(),
});
export const AttachmentMeta = z.object({
    /**
     * Provider attachment id, what `getAttachment` takes; null when it cannot be downloaded. Gmail:
     * the MIME part id (stable: Gmail issues a new `attachmentId` on every read); Graph: the
     * attachment id.
     */
    id: z.string().nullable(),
    name: z.string(),
    size: z.number().int().nonnegative(),
    mimeType: z.string(),
    inline: z.boolean(),
    /** The part's Content-ID without angle brackets, which the HTML references as `cid:…`. */
    contentId: z.string().optional(),
});
/** Message metadata: what the Firestore cache holds (§6.4). */
export const NormalizedMessage = z.object({
    id: z.string().min(1),
    threadId: z.string().min(1),
    /** RFC 5322 Message-ID, needed to thread replies; null when the provider did not return it. */
    internetMessageId: z.string().nullable(),
    from: Address,
    to: z.array(Address),
    cc: z.array(Address),
    subject: z.string(),
    snippet: z.string(),
    date: z.date(),
    location: MailLocation,
    /** Display name of the user folder when `location` is `folder`, otherwise null. */
    folderName: z.string().nullable(),
    /** Gmail user label names / Graph category names. */
    tags: z.array(z.string()),
    unread: z.boolean(),
    starred: z.boolean(),
    hasAttachments: z.boolean(),
    /** Empty in metadata syncs where the provider does not list parts (Gmail format=metadata). */
    attachments: z.array(AttachmentMeta),
});
export const MessageBody = z.object({
    html: z.string().nullable(),
    text: z.string().nullable(),
});
/** A message as `getMessage` returns it: metadata, plus the body for `part: 'full'`. */
export const MailMessage = z.object({
    meta: NormalizedMessage,
    body: MessageBody.nullable(),
});
/** A thread summary folded from its messages (`foldThread` in `@kwp/domain`). */
export const NormalizedThread = z.object({
    id: z.string().min(1),
    subject: z.string(),
    snippet: z.string(),
    participants: z.array(Address),
    lastMessageAt: z.date(),
    location: MailLocation,
    folderName: z.string().nullable(),
    tags: z.array(z.string()),
    unread: z.boolean(),
    starred: z.boolean(),
    hasAttachments: z.boolean(),
    messageIds: z.array(z.string()),
    /** Who sent the newest message that is not a draft (the list shows "You:" when it was the mailbox). */
    lastFrom: Address.nullable(),
    /** When the mailbox last sent in the thread (its newest sent message); null when it never did. */
    sentAt: z.date().nullable(),
    /** `person` when any message not from the owner is; else the newest such message's kind (§6.7). */
    kind: MessageKind,
    kindReason: KindReason,
    /** What the thread's newest message from a known app is about (§6.7, WP-062); null for other mail. */
    source: ThreadSource.nullable(),
    /** Every GitHub reason of its messages, and the group they place the thread in (null: not GitHub). */
    ghReasons: z.array(GhReason),
    ghGroup: GhGroup.nullable(),
    /** A person's by default alone, and never answered (§6.7, WP-063): the row is marked "New". */
    newSender: z.boolean(),
});
/**
 * One page of a full sync. `messages` are upserts; `threadIds` are the threads those upserts and
 * deletions touched, which the worker re-folds from its cache. `cursor` is set on the last batch
 * only: a full sync is complete when a batch carries a cursor. Every other batch carries `resume`,
 * the provider's own opaque token: passed back to `fullSync`, it continues with the batch after
 * this one (§6.3.2), so a full sync can span many requests.
 */
export const SyncBatch = z.object({
    messages: z.array(NormalizedMessage),
    deletedMessageIds: z.array(z.string()),
    threadIds: z.array(z.string()),
    cursor: SyncCursor.nullable(),
    resume: z.string().nullable(),
    /** Every header and provider field of each upserted message (§6.4); one per message the provider described. */
    raw: z.array(MessageRaw),
});
/**
 * One folder an incremental sync had to read again from scratch (Graph: its delta link expired,
 * 410 / syncStateNotFound). `messageIds` is everything the folder holds now, so the worker removes
 * cached messages it had in that folder that are neither listed here nor upserted elsewhere.
 */
export const ResyncedFolder = z.object({
    location: MailLocation,
    /** The user folder's display name when `location` is `folder`, otherwise null. */
    folderName: z.string().nullable(),
    messageIds: z.array(z.string()),
});
/** The result of one incremental sync: every change since the cursor, and the next cursor. */
export const SyncResult = z.object({
    messages: z.array(NormalizedMessage),
    deletedMessageIds: z.array(z.string()),
    threadIds: z.array(z.string()),
    cursor: SyncCursor,
    /** Every header and provider field of each upserted message (§6.4). */
    raw: z.array(MessageRaw),
    /** Folders read again in full because their own cursor expired; absent when none were. */
    resyncedFolders: z.array(ResyncedFolder).optional(),
});
const MessageIds = z.array(z.string().min(1)).min(1);
/**
 * One mail mutation over provider message ids (Gmail labels belong to messages, so even thread
 * actions arrive here as message ids, §6.2).
 */
export const MailOp = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("archive"), messageIds: MessageIds }),
    z.object({ kind: z.literal("moveToInbox"), messageIds: MessageIds }),
    z.object({ kind: z.literal("addTags"), messageIds: MessageIds, tags: z.array(z.string().min(1)).min(1) }),
    z.object({ kind: z.literal("removeTags"), messageIds: MessageIds, tags: z.array(z.string().min(1)).min(1) }),
    z.object({ kind: z.literal("markRead"), messageIds: MessageIds }),
    z.object({ kind: z.literal("markUnread"), messageIds: MessageIds }),
    z.object({ kind: z.literal("star"), messageIds: MessageIds }),
    z.object({ kind: z.literal("unstar"), messageIds: MessageIds }),
    z.object({ kind: z.literal("spam"), messageIds: MessageIds }),
    z.object({ kind: z.literal("notSpam"), messageIds: MessageIds }),
    z.object({ kind: z.literal("trash"), messageIds: MessageIds }),
    z.object({ kind: z.literal("untrash"), messageIds: MessageIds }),
    z.object({ kind: z.literal("moveToFolder"), messageIds: MessageIds, folder: z.string().min(1) }),
]);
export const OpResult = z.object({
    kind: z.string(),
    ok: z.array(z.string()),
    failed: z.array(z.object({ id: z.string(), reason: z.string() })),
});
export const SearchPage = z.object({
    messages: z.array(NormalizedMessage),
    nextPage: z.string().nullable(),
});
export const Draft = z
    .object({
    to: z.array(Address).min(1),
    cc: z.array(Address).default([]),
    bcc: z.array(Address).default([]),
    subject: z.string(),
    text: z.string().nullable(),
    html: z.string().nullable(),
    /** Provider id of the message this replies to; null for a new conversation. */
    inReplyTo: z.string().nullable().default(null),
})
    .refine((d) => d.text !== null || d.html !== null, { message: "a draft needs a text or html body" });
export const SentRef = z.object({
    /** Provider id of the sent message; Graph's sendMail does not return one (null). */
    id: z.string().nullable(),
    threadId: z.string().nullable(),
});
/** Provider push state after `watch.start` / `watch.renew` (Gmail watch, Graph subscription). */
export const WatchState = z.object({
    expiresAt: z.date(),
    subscriptionId: z.string().nullable(),
    /** Gmail returns the mailbox's current historyId with every watch call. */
    historyId: z.string().nullable(),
});
/**
 * A native provider rule as the adapters see it (Gmail filter / Outlook inbox rule), §7. `list`
 * returns one per provider object (a Gmail rule with two user labels is two filters); `create`
 * takes a whole rule and returns the ids of everything it created.
 */
export const ProviderRuleAction = RuleAction;
export const ProviderRule = z.object({
    /** Provider id (Gmail filter id / Graph messageRule id); null before creation. */
    id: z.string().nullable(),
    match: QueryNode,
    actions: z.array(RuleAction).min(1),
});
/** What `rules.create` takes: the rule's name (Graph shows it), match and actions. */
export const ProviderRuleInput = z.object({
    name: z.string().min(1),
    match: QueryNode,
    actions: z.array(RuleAction).min(1),
});
//# sourceMappingURL=mail.js.map